import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { MockBadge } from '../components/MockBadge';
import { PageHeader } from '../components/PageHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/Select';
import { DateRangePicker, choiceCompareRange } from '../components/stats/DateRangePicker';
import { DeliverabilityCard } from '../components/stats/DeliverabilityCard';
import { Funnel, KpiTiles, ReportTable } from '../components/stats/ReportParts';
import { buildReport, type RowAction, type SortState } from '../components/stats/aggregate';
import { formatRange, rangeToPeriod, todayKey } from '../components/stats/dateRange';
import {
  parseStatsParams,
  writeStatsParams,
  type StatsLevel,
  type StatsQuery,
} from '../components/stats/statsParams';
import { apiFetch, fetchPeriodStats } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AUTO_LABEL_NAMES } from '../lib/automationLabels';
import {
  formatCost,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatPercent,
  formatRoi,
  formatSendPrice,
} from '../lib/format';
import { cn } from '../lib/utils';
import {
  ApiError,
  type AutoLabel,
  type CampaignOrder,
  type CampaignStatsDetail,
  type CampaignStatsListItem,
  type LabelsOverviewResponse,
  type SendingRoi,
} from '../types/api';

/** Phase 6 A/B variant stats + optional winner badge. */
function AbTestCard({ detail, status }: { detail: CampaignStatsDetail; status: string }) {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const pickMutation = useMutation({
    mutationFn: (variant: 'a' | 'b') =>
      apiFetch<{ ok: true }>(`/campaigns/${detail.id}/ab-winner`, {
        method: 'POST',
        body: { variant },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stats', 'campaign', detail.id] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
  const ab = detail.ab;
  if (!ab) return null;
  // K6: auto-send off → the rest wait for an admin to pick the winner.
  const waitingForPick =
    ab.autoSend === false && status === 'sending' && !ab.winnerVariant && !ab.noSignificantWinner;
  const pickError =
    pickMutation.error instanceof ApiError && pickMutation.error.body.message
      ? pickMutation.error.body.message
      : pickMutation.isError
        ? 'The winner could not be sent. Try again.'
        : null;
  const rate = (v: { recipients: number; uniqueClicks: number }) =>
    v.recipients > 0 ? v.uniqueClicks / v.recipients : 0;
  return (
    <Card className="p-4 flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold m-0">
          {ab.contentDiffers
            ? ab.subjectB
              ? 'A/B test of subject line and content'
              : 'A/B test of content'
            : 'A/B test of subject line'}
        </h3>
        {ab.winnerVariant ? (
          <span className="text-[11px] rounded-full px-2 py-0.5 bg-success-bg text-success-text">
            Variant {ab.winnerVariant.toUpperCase()} won
          </span>
        ) : ab.noSignificantWinner ? (
          <span className="text-[11px] rounded-full px-2 py-0.5 bg-bg text-muted">
            No clear winner — the rest got variant A
          </span>
        ) : waitingForPick ? (
          <span className="text-[11px] rounded-full px-2 py-0.5 bg-bg text-muted">
            The rest wait for you to pick the winner
          </span>
        ) : (
          <span className="text-[11px] rounded-full px-2 py-0.5 bg-bg text-muted">
            Test is still running
          </span>
        )}
      </div>
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="text-left text-muted">
            <th className="font-normal py-1.5">Variant</th>
            <th className="font-normal py-1.5">Subject line</th>
            <th className="font-normal py-1.5 text-right">Recipients</th>
            <th className="font-normal py-1.5 text-right">Unique clicks</th>
            <th className="font-normal py-1.5 text-right">Click rate</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-border-soft">
            <td className="py-1.5 font-medium">A</td>
            <td className="py-1.5">{detail.name}</td>
            <td className="py-1.5 text-right font-mono">{formatNumber(ab.stats.a.recipients)}</td>
            <td className="py-1.5 text-right font-mono">{formatNumber(ab.stats.a.uniqueClicks)}</td>
            <td className="py-1.5 text-right font-mono">{formatPercent(rate(ab.stats.a))}</td>
          </tr>
          <tr className="border-t border-border-soft">
            <td className="py-1.5 font-medium">B</td>
            <td className="py-1.5">{ab.subjectB ?? (ab.contentDiffers ? 'Same as A' : '—')}</td>
            <td className="py-1.5 text-right font-mono">{formatNumber(ab.stats.b.recipients)}</td>
            <td className="py-1.5 text-right font-mono">{formatNumber(ab.stats.b.uniqueClicks)}</td>
            <td className="py-1.5 text-right font-mono">{formatPercent(rate(ab.stats.b))}</td>
          </tr>
        </tbody>
      </table>
      {ab.contentDiffers ? (
        <p className="text-xs text-muted m-0" data-testid="ab-content-differs">
          The content differed: group B got its own version of the email
          {ab.subjectB ? ' as well as its own subject line' : ' with the same subject line'}.
          {ab.winnerVariant ? ' The rest got the winning version.' : ''}
        </p>
      ) : null}
      {waitingForPick && isAdmin ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted m-0">
            Sends the chosen {ab.contentDiffers ? 'version' : 'subject line'} to everyone who has
            not had the mail yet. This cannot be undone.
          </p>
          <div className="flex flex-wrap gap-2">
            {(['a', 'b'] as const).map((v) => (
              <Button
                key={v}
                variant="secondary"
                disabled={pickMutation.isPending}
                onClick={() => pickMutation.mutate(v)}
              >
                Send variant {v.toUpperCase()} to the rest
              </Button>
            ))}
          </div>
          {pickError ? <Alert>{pickError}</Alert> : null}
        </div>
      ) : null}
    </Card>
  );
}

/** Phase 6 "Resend to non-clickers" — editor+ only, only for a sent campaign. */
function ResendNonClickersCard({ campaignId, status }: { campaignId: string; status: string }) {
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  const resendMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ campaign: { id: string } }>(`/campaigns/${campaignId}/resend-non-clickers`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  if (!isEditor || status !== 'sent') return null;

  const errorMsg =
    resendMutation.error instanceof ApiError && resendMutation.error.body.message
      ? resendMutation.error.body.message
      : resendMutation.isError
        ? 'Resend could not be created. Try again.'
        : null;

  return (
    <Card className="p-4 flex flex-col gap-2.5">
      <h3 className="text-sm font-semibold m-0">Resend to non-clickers</h3>
      <p className="text-xs text-muted m-0">
        Creates a new draft with the same content, targeted at recipients who got the campaign but
        did not click — anyone who has since unsubscribed/blocked/bounced is excluded. Can be done
        at the earliest 24 hours after sending, and only once per campaign. Goes through the normal
        approval.
      </p>
      {resendMutation.isSuccess ? (
        <Link
          to={`/kampagner/${resendMutation.data.campaign.id}`}
          className="text-sm font-medium text-accent"
        >
          Open the new draft →
        </Link>
      ) : (
        <Button
          variant="secondary"
          disabled={resendMutation.isPending}
          onClick={() => resendMutation.mutate()}
        >
          {resendMutation.isPending ? 'Creating…' : 'Resend to non-clickers'}
        </Button>
      )}
      {errorMsg ? <Alert>{errorMsg}</Alert> : null}
    </Card>
  );
}

function ClicksChart({ detail }: { detail: CampaignStatsDetail }) {
  const [asTable, setAsTable] = useState(false);
  const hours = detail.clicksPerHour;
  const max = Math.max(1, ...hours.map((h) => h.clicks));
  const totalClicks = hours.reduce((sum, h) => sum + h.clicks, 0);
  const first4h = hours.slice(0, 4).reduce((sum, h) => sum + h.clicks, 0);
  const share = totalClicks > 0 ? first4h / totalClicks : 0;

  return (
    <section
      aria-labelledby="chart-heading"
      className="flex-[3_1_0] min-w-[260px] border border-border rounded-xl p-4 flex flex-col gap-2.5"
    >
      <div className="flex items-center">
        <h2 id="chart-heading" className="m-0 text-sm font-semibold">
          Clicks per hour, first 48 hours
        </h2>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setAsTable((v) => !v)}>
          {asTable ? 'Show as chart' : 'Show as table'}
        </Button>
      </div>
      <p className="m-0 text-xs text-muted">
        {share > 0
          ? `About ${formatPercent(share)} of all clicks happened within the first 4 hours.`
          : 'No clicks recorded yet.'}
      </p>
      {asTable ? (
        <div className="max-h-[220px] overflow-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-muted">
                <th className="font-normal py-1.5">Hour</th>
                <th className="font-normal py-1.5 text-right">Clicks</th>
              </tr>
            </thead>
            <tbody>
              {hours.map((h, i) => (
                <tr key={h.hourStart} className="border-t border-border-soft">
                  <td className="py-1.5">t+{i}</td>
                  <td className="py-1.5 text-right font-mono">{formatNumber(h.clicks)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <svg
          role="img"
          aria-label={`Bar chart: clicks per hour in the first 48 hours, ${formatNumber(totalClicks)} clicks total, declining from ${formatNumber(hours[0]?.clicks ?? 0)} to ${formatNumber(hours[hours.length - 1]?.clicks ?? 0)}`}
          viewBox="0 0 480 150"
          preserveAspectRatio="none"
          className="w-full h-[150px] border-b border-border"
        >
          {hours.map((h, i) => {
            const barWidth = 480 / hours.length - 1.5;
            const barHeight = (h.clicks / max) * 148;
            return (
              <rect
                key={h.hourStart}
                x={i * (480 / hours.length)}
                y={150 - barHeight}
                width={Math.max(0, barWidth)}
                height={barHeight}
                fill="#5457D6"
                rx={1.5}
              />
            );
          })}
        </svg>
      )}
      <div className="flex text-[11px] text-muted">
        <span>0 t</span>
        <span className="ml-auto">24 t</span>
        <span className="ml-auto">48 t</span>
      </div>
    </section>
  );
}

/**
 * What the campaign cost to send and what it earned (26 Oct 2026): the mails
 * handed to Amazon SES at the price per 1,000 set under Settings → Senders,
 * and every order attributed to it, net of refunds. Kept after the 90-day
 * message archive.
 */
function CampaignRoiCards({ roi }: { roi: SendingRoi }) {
  return (
    <section aria-labelledby="campaign-roi-heading" className="flex flex-col gap-2.5">
      <h2 id="campaign-roi-heading" className="m-0 text-sm font-semibold">
        Sending cost and return
      </h2>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
        <Card className="p-3.5">
          <div className="text-muted text-xs">Sending cost</div>
          <div className="text-[22px] font-semibold mt-1">{formatCost(roi.cost, roi.currency)}</div>
          <div className="text-muted text-xs">
            {formatNumber(roi.sent)} emails at {formatSendPrice(roi.costPerThousand, roi.currency)}{' '}
            per 1,000
          </div>
        </Card>
        <Card className="p-3.5">
          <div className="text-muted text-xs">Orders</div>
          <div className="text-[22px] font-semibold mt-1">{formatNumber(roi.orders)}</div>
          <div className="text-muted text-xs">
            attributed to this email
            {roi.onHoldOrders > 0 ? ` (+${formatNumber(roi.onHoldOrders)} on hold)` : ''}
          </div>
        </Card>
        <Card className="p-3.5">
          <div className="text-muted text-xs">Revenue</div>
          <div className="text-[22px] font-semibold mt-1">
            {formatMoney(roi.revenue, roi.currency)}
          </div>
          <div className="text-muted text-xs">
            net of refunds
            {roi.onHoldOrders > 0
              ? ` (+${formatMoney(roi.onHoldRevenue, roi.currency)} on hold)`
              : ''}
          </div>
        </Card>
        <Card className="p-3.5">
          <div className="text-muted text-xs">ROI</div>
          <div className="text-[22px] font-semibold mt-1">{formatRoi(roi.roi)}</div>
          <div className="text-muted text-xs">
            earned per {formatMoney(1, roi.currency)} spent on sending
          </div>
        </Card>
      </div>
    </section>
  );
}

/**
 * Who bought from the campaign (Mikkel, 26 Oct 2026): order number, time,
 * buyer and amount net of refunds, newest first. The server sends at most
 * 100; `total` is the full count from the ROI cards.
 */
function CampaignOrders({ orders, total }: { orders: CampaignOrder[]; total: number }) {
  if (orders.length === 0) return null;
  return (
    <section
      aria-labelledby="campaign-orders-heading"
      className="border border-border rounded-xl p-4"
    >
      <h2 id="campaign-orders-heading" className="m-0 mb-2.5 text-sm font-semibold">
        Orders from this email
      </h2>
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="text-left text-muted border-b border-border-soft">
            <th className="py-1.5 pr-3 font-normal">Order</th>
            <th className="py-1.5 pr-3 font-normal">Placed</th>
            <th className="py-1.5 pr-3 font-normal">Customer</th>
            <th className="py-1.5 font-normal text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.orderId} className="border-b border-border-soft last:border-b-0">
              <td className="py-1.5 pr-3 tabular-nums">#{o.orderId}</td>
              <td className="py-1.5 pr-3 text-muted">{formatDateTime(o.orderedAt)}</td>
              <td className="py-1.5 pr-3 break-all">
                {o.contactId ? (
                  <Link
                    to={`/kontakter/${o.contactId}`}
                    className="text-link underline-offset-2 hover:underline"
                  >
                    {o.email}
                  </Link>
                ) : (
                  <span className="text-muted">Contact deleted</span>
                )}
              </td>
              <td className="py-1.5 text-right tabular-nums">
                {formatMoney(o.amount, o.currency, 2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > orders.length ? (
        <p className="m-0 mt-2 text-muted text-xs">
          Showing the newest {formatNumber(orders.length)} of {formatNumber(total)} orders.
        </p>
      ) : null}
    </section>
  );
}

/**
 * One campaign in detail (KPIs, clicks per hour, top links, A/B test,
 * resend) plus the campaign-vs-campaign table. Opened from the report with
 * `?campaign=<id>`; the report's own params stay in the URL so "Back" lands
 * on the same period.
 */
function CampaignDetail({ campaignId }: { campaignId: string }) {
  const [params, setParams] = useSearchParams();
  const listQuery = useQuery({
    queryKey: ['stats', 'campaigns'],
    queryFn: () => apiFetch<{ items: CampaignStatsListItem[] }>('/stats/campaigns'),
  });
  const items = listQuery.data?.items ?? [];
  const activeId = campaignId;

  const detailQuery = useQuery({
    queryKey: ['stats', 'campaign', activeId],
    queryFn: () => apiFetch<CampaignStatsDetail>(`/stats/campaigns/${activeId}`),
  });

  const kpis = useMemo(() => {
    const d = detailQuery.data;
    if (!d) return null;
    return {
      delivered: d.delivered,
      deliveredOf: d.sent,
      clickRate: d.clicked / Math.max(1, d.delivered),
      clickCount: d.clicked,
      unsubRate: d.unsubscribed / Math.max(1, d.delivered),
      unsubCount: d.unsubscribed,
      bounceRate: d.bounced / Math.max(1, d.sent),
      complaintRate: d.complained / Math.max(1, d.delivered),
    };
  }, [detailQuery.data]);

  function setCampaign(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set('campaign', id);
    else next.delete('campaign');
    setParams(next);
  }

  return (
    <>
      <header className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" className="-ml-3" onClick={() => setCampaign(null)}>
          <ArrowLeft size={16} aria-hidden="true" />
          Back to report
        </Button>
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Statistics</h1>
        <MockBadge />
        <div className="ml-auto flex items-center gap-2 max-sm:w-full">
          <label htmlFor="campaign-select" className="text-[13px] text-muted">
            Campaign
          </label>
          <Select value={activeId} onValueChange={setCampaign}>
            <SelectTrigger
              id="campaign-select"
              className="max-w-[260px] max-sm:max-w-none max-sm:flex-grow"
            >
              <SelectValue placeholder="Select campaign" />
            </SelectTrigger>
            <SelectContent>
              {items.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                  {c.sentAt ? ` · ${formatDate(c.sentAt)}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {detailQuery.isError ? (
        <Alert>There are no detailed statistics for this campaign.</Alert>
      ) : !kpis ? (
        <div className="text-muted">Loading statistics…</div>
      ) : (
        <>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
            <Card className="p-3.5">
              <div className="text-muted text-xs">Delivered</div>
              <div className="text-[22px] font-semibold mt-1">{formatNumber(kpis.delivered)}</div>
              <div className="text-muted text-xs">of {formatNumber(kpis.deliveredOf)}</div>
            </Card>
            <Card className="p-3.5">
              <div className="text-muted text-xs">Clicked</div>
              <div className="text-[22px] font-semibold mt-1">{formatPercent(kpis.clickRate)}</div>
              <div className="text-muted text-xs">{formatNumber(kpis.clickCount)} people</div>
            </Card>
            <Card className="p-3.5">
              <div className="text-muted text-xs">Unsubscribed</div>
              <div className="text-[22px] font-semibold mt-1">{formatPercent(kpis.unsubRate)}</div>
              <div className="text-muted text-xs">{formatNumber(kpis.unsubCount)} people</div>
            </Card>
            <Card className="p-3.5">
              <div className="text-muted text-xs">Bounce</div>
              <div className="text-[22px] font-semibold mt-1">{formatPercent(kpis.bounceRate)}</div>
              <div
                className={
                  kpis.bounceRate < 0.02 ? 'text-success-text text-xs' : 'text-danger-text text-xs'
                }
              >
                {kpis.bounceRate < 0.02 ? '✓' : '✗'} limit 2%
              </div>
            </Card>
            <Card className="p-3.5">
              <div className="text-muted text-xs">Complaints</div>
              <div className="text-[22px] font-semibold mt-1">
                {formatPercent(kpis.complaintRate)}
              </div>
              <div
                className={
                  kpis.complaintRate < 0.001
                    ? 'text-success-text text-xs'
                    : 'text-danger-text text-xs'
                }
              >
                {kpis.complaintRate < 0.001 ? '✓' : '✗'} limit 0.1%
              </div>
            </Card>
          </div>

          {detailQuery.data?.roi ? <CampaignRoiCards roi={detailQuery.data.roi} /> : null}
          {detailQuery.data?.orders ? (
            <CampaignOrders
              orders={detailQuery.data.orders}
              total={detailQuery.data.roi?.orders ?? detailQuery.data.orders.length}
            />
          ) : null}

          <div className="flex flex-wrap gap-3">
            {detailQuery.data ? <ClicksChart detail={detailQuery.data} /> : null}
            <section
              aria-labelledby="links-heading"
              className="flex-[2_1_0] min-w-[260px] border border-border rounded-xl p-4"
            >
              <h2 id="links-heading" className="m-0 mb-2.5 text-sm font-semibold">
                Most clicked links
              </h2>
              <table className="w-full border-collapse text-[13px]">
                <tbody>
                  {detailQuery.data?.topLinks.map((link) => (
                    <tr key={link.url} className="border-b border-border-soft last:border-b-0">
                      <td className="py-2 break-all">{link.url}</td>
                      <td className="py-2 text-right font-mono">{formatNumber(link.clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {detailQuery.data && !detailQuery.data.opensTracked ? (
                <div className="mt-3 px-3 py-2.5 rounded-[10px] bg-bg text-[12px] text-zinc-700">
                  Opens are not measured (tracking pixel disabled for privacy reasons).
                </div>
              ) : null}
            </section>
          </div>

          {detailQuery.data ? (
            <div className="flex flex-wrap gap-3">
              <div className="flex-[3_1_0] min-w-[260px]">
                <AbTestCard
                  detail={detailQuery.data}
                  status={items.find((i) => i.id === activeId)?.status ?? ''}
                />
              </div>
              <div className="flex-[2_1_0] min-w-[260px]">
                <ResendNonClickersCard
                  campaignId={activeId}
                  status={items.find((i) => i.id === activeId)?.status ?? ''}
                />
              </div>
            </div>
          ) : null}

          <section
            aria-labelledby="compare-heading"
            className="border border-border rounded-xl overflow-hidden"
          >
            <h2
              id="compare-heading"
              className="m-0 px-4 py-3 text-sm font-semibold border-b border-border-soft"
            >
              Campaign vs campaign
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-[13px]">
                <thead>
                  <tr className="text-left text-muted">
                    <th className="font-normal px-4 py-2">Campaign</th>
                    <th className="font-normal px-2 py-2 text-right">Delivered</th>
                    <th className="font-normal px-2 py-2 text-right">Clicks</th>
                    <th className="font-normal px-2 py-2 text-right">Unsubscribed</th>
                    <th className="font-normal px-2 py-2 text-right">Bounce</th>
                    <th className="font-normal px-4 py-2 text-right">Complaints</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} className="border-t border-border-soft">
                      <td className="px-4 py-2.5 font-medium">{c.name}</td>
                      <td className="px-2 py-2.5 text-right font-mono">
                        {formatNumber(c.delivered)}
                      </td>
                      <td className="px-2 py-2.5 text-right font-mono">
                        {formatPercent(c.clicked / Math.max(1, c.delivered))}
                      </td>
                      <td className="px-2 py-2.5 text-right font-mono">
                        {formatPercent(c.unsubscribed / Math.max(1, c.delivered))}
                      </td>
                      <td className="px-2 py-2.5 text-right font-mono">
                        {formatPercent(c.bounced / Math.max(1, c.sent))}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">
                        {formatPercent(c.complained / Math.max(1, c.delivered))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

const LEVELS: { value: StatsLevel; label: string }[] = [
  { value: 'all', label: 'All emails' },
  { value: 'campaigns', label: 'Campaigns' },
  { value: 'automations', label: 'Automations' },
];

const AUTO_ORDER = Object.keys(AUTO_LABEL_NAMES);

function labelName(label: string): string {
  return AUTO_LABEL_NAMES[label as AutoLabel] ?? label;
}

/** The period report: filters, KPI tiles, funnel and a sortable table on three levels. */
function PeriodReport() {
  const [params, setParams] = useSearchParams();
  const [today] = useState(() => todayKey());
  const query = parseStatsParams(params, today);
  const [sort, setSort] = useState<SortState>({ key: 'sent', dir: 'desc' });

  const setQuery = (patch: Partial<StatsQuery>) =>
    setParams(writeStatsParams({ ...query, ...patch }, params));

  const choice = { range: query.range, compare: query.compare, customCompare: query.customCompare };
  const cmpRange = choiceCompareRange(choice);
  const period = rangeToPeriod(query.range);
  const cmpPeriod = cmpRange ? rangeToPeriod(cmpRange) : null;

  const labelsQuery = useQuery({
    queryKey: ['labels'],
    queryFn: () => apiFetch<LabelsOverviewResponse>('/labels'),
  });
  const labelOptions = useMemo(() => {
    const labels = (labelsQuery.data?.counts ?? []).map((c) => c.label);
    if (query.label && !labels.includes(query.label)) labels.push(query.label);
    const rank = (l: string) => (AUTO_ORDER.includes(l) ? AUTO_ORDER.indexOf(l) : 100);
    return labels.sort((a, b) => rank(a) - rank(b) || labelName(a).localeCompare(labelName(b)));
  }, [labelsQuery.data, query.label]);

  const curQuery = useQuery({
    queryKey: ['stats', 'period', period.from, period.to, query.label],
    queryFn: ({ signal }) => fetchPeriodStats(period, query.label, signal),
  });
  const prevQuery = useQuery({
    queryKey: ['stats', 'period', cmpPeriod?.from, cmpPeriod?.to, query.label],
    queryFn: ({ signal }) =>
      fetchPeriodStats(cmpPeriod as { from: number; to: number }, query.label, signal),
    enabled: cmpPeriod !== null,
  });

  const cur = curQuery.data;
  const prev = cmpPeriod ? (prevQuery.data ?? null) : null;
  const report = useMemo(
    () => (cur ? buildReport(cur, prev, query.level, query.flow) : null),
    [cur, prev, query.level, query.flow],
  );
  const currency = cur?.currency ?? 'EUR';
  const nothingSent =
    !!cur && cur.items.every((i) => i.sent === 0) && cur.automationOrders.length === 0;

  function onAction(a: RowAction) {
    if (a.kind === 'level') setQuery({ level: a.level, flow: null });
    else if (a.kind === 'flow') setQuery({ level: 'automations', flow: a.automationId });
    else {
      const next = new URLSearchParams(params);
      next.set('campaign', a.campaignId);
      setParams(next);
    }
  }

  const tableTitle = report?.flow
    ? `Emails in ${report.flow.name}`
    : (LEVELS.find((l) => l.value === query.level)?.label ?? 'All emails');

  return (
    <>
      <PageHeader
        title="Statistics"
        badge={<MockBadge />}
        description={
          <>
            Showing {formatRange(query.range)}
            {cmpRange ? ` compared with ${formatRange(cmpRange)}` : ''}
            {query.label ? `, only contacts tagged “${labelName(query.label)}”` : ''}.
          </>
        }
        actions={
          <div className="flex flex-wrap items-end gap-x-3 gap-y-2.5 max-sm:w-full">
            <div className="flex flex-col gap-1 max-sm:w-full">
              <span className="text-xs text-muted">Period</span>
              <DateRangePicker
                value={choice}
                today={today}
                onApply={(c) =>
                  setQuery({ range: c.range, compare: c.compare, customCompare: c.customCompare })
                }
              />
            </div>
            <div className="flex flex-col gap-1 max-sm:w-full">
              <label htmlFor="tag-select" className="text-xs text-muted">
                Customer tag
              </label>
              <Select
                value={query.label ?? 'all'}
                onValueChange={(v) => setQuery({ label: v === 'all' ? null : v })}
              >
                <SelectTrigger id="tag-select" className="min-w-[180px] max-sm:w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All contacts</SelectItem>
                  {labelOptions.map((l) => (
                    <SelectItem key={l} value={l}>
                      {labelName(l)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        }
      />

      {curQuery.isError || prevQuery.isError ? (
        <Alert>The statistics could not be loaded. Try again in a moment.</Alert>
      ) : !report || !cur ? (
        <div className="text-muted">Loading statistics…</div>
      ) : nothingSent ? (
        <Card className="p-8 text-center">
          <p className="m-0 font-medium">Nothing was sent in this period.</p>
          <p className="m-0 mt-1 text-sm text-muted">
            Pick a longer period{query.label ? ' or another customer tag' : ''} to see numbers.
          </p>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div
              role="group"
              aria-label="Level"
              className="inline-flex gap-0.5 rounded-[9px] border border-border bg-bg p-[3px]"
            >
              {LEVELS.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  aria-pressed={query.level === l.value}
                  onClick={() => setQuery({ level: l.value, flow: null })}
                  className={cn(
                    'rounded-[7px] px-3 py-1.5 text-sm font-medium text-muted',
                    query.level === l.value && 'bg-card text-text shadow-sm',
                  )}
                >
                  {l.label}
                </button>
              ))}
            </div>
            {report.flow ? (
              <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[13px]">
                <button
                  type="button"
                  className="text-link hover:underline"
                  onClick={() => setQuery({ level: 'automations', flow: null })}
                >
                  Automations
                </button>
                <span aria-hidden="true" className="text-muted">
                  ›
                </span>
                <b aria-current="page">{report.flow.name}</b>
              </nav>
            ) : null}
          </div>

          <KpiTiles cur={report.total} prev={report.totalPrev} currency={currency} />

          {report.opensIncomplete ? (
            <p className="m-0 text-xs text-muted">
              Open tracking is off for some senders, so open rate is incomplete.{' '}
              <Link to="/indstillinger/afsendere">Senders</Link>
            </p>
          ) : null}

          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] max-[860px]:grid-cols-1 gap-4 items-start">
            <Card className="p-4 flex flex-col gap-3 min-w-0">
              <h2 className="m-0 text-base font-semibold">Funnel</h2>
              <Funnel cur={report.total} prev={report.totalPrev} />
            </Card>
            <Card className="p-4 flex flex-col gap-3 min-w-0">
              <h2 className="m-0 text-base font-semibold">{tableTitle}</h2>
              {report.rows.length > 0 ? (
                <ReportTable
                  rows={report.rows}
                  sort={sort}
                  onSort={setSort}
                  onAction={onAction}
                  currency={currency}
                  {...(query.level === 'all'
                    ? { total: { cur: report.total, prev: report.totalPrev } }
                    : {})}
                />
              ) : (
                <p className="m-0 p-5 text-center text-muted">
                  {query.level === 'campaigns'
                    ? 'No campaigns were sent in this period.'
                    : 'No automation emails were sent in this period.'}
                </p>
              )}
              <p className="m-0 text-xs text-muted">
                Click a column to sort.
                {prev
                  ? ' Campaigns are compared with the average campaign in the other period. Automations are compared with themselves.'
                  : ''}
              </p>
            </Card>
          </div>
        </>
      )}

      <DeliverabilityCard />
    </>
  );
}

export function StatistikPage() {
  const [params] = useSearchParams();
  const campaignId = params.get('campaign');
  return campaignId ? <CampaignDetail campaignId={campaignId} /> : <PeriodReport />;
}
